import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, MapPin, Phone, Mail, MessageSquare, Trash2, Repeat, Clock, Wrench, Users, PhoneCall, CalendarDays, LayoutGrid, UserPlus, Receipt, Map as MapIcon, ShieldCheck } from "lucide-react";
import InvoiceManager from "@/components/InvoiceManager";
import PoliciesPanel from "@/components/PoliciesPanel";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FeatureGate } from "@/components/FeatureGate";
import ClientDialog from "@/components/ClientDialog";
import ServiceLoopDialog from "@/components/ServiceLoopDialog";
import CheckInPanel from "@/components/CheckInPanel";
import DayPlanner from "@/components/DayPlanner";
import TaskBoard from "@/components/TaskBoard";
import CrewManager from "@/components/CrewManager";
import TodaySummary from "@/components/TodaySummary";
import TeamOverview from "@/components/TeamOverview";
import TurfMap from "@/components/TurfMap";

const CH_ICON = { email: Mail, sms: MessageSquare, voice: Phone };

export default function BusinessMode({ clients, reload }) {
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "clients");
  const [clientDialog, setClientDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loopFor, setLoopFor] = useState(null);
  const [editLoop, setEditLoop] = useState(null);

  const { user } = useAuth();
  const business = user?.role === "admin" || user?.business_active;
  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  const removeClient = async (c) => {
    await api.delete(`/clients/${c.id}`);
    toast.success("Client removed");
    reload();
  };
  const removeLoop = async (id) => {
    await api.delete(`/loops/${id}`);
    toast.success("Service loop removed");
    reload();
  };

  if (!business) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <FeatureGate locked testid="gate-business" title="Business dispatch centre" tier="Business Platinum" blurb="Clients, service loops & omni-channel dispatch." cta="Upgrade to unlock">
          <div />
        </FeatureGate>
        <div className="mt-10" data-testid="payg-billing-hub">
          <p className="label-mono text-[var(--text-2)] mb-3">Or pay as you go — send invoices with credits</p>
          <InvoiceManager />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="label-mono text-[var(--text-2)]">Business dispatch centre</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-2">
            Field service, <span className="italic text-[var(--accent)]">automated</span>.
          </h1>
        </div>
        <button
          data-testid="add-client"
          onClick={() => { setEditing(null); setClientDialog(true); }}
          className="flex items-center gap-2 h-12 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" /> Add client
        </button>
      </div>

      <TodaySummary />

      <div className="flex gap-2 mb-6 overflow-x-auto scroll-thin flex-nowrap -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="business-tabs">
        <TabBtn active={tab === "clients"} onClick={() => setTab("clients")} testid="tab-clients" icon={Users}>Clients</TabBtn>
        <TabBtn active={tab === "invoices"} onClick={() => setTab("invoices")} testid="tab-invoices" icon={Receipt}>Billing Hub</TabBtn>
        <TabBtn active={tab === "checkins"} onClick={() => setTab("checkins")} testid="tab-checkins" icon={PhoneCall}>Check-Ins</TabBtn>
        <TabBtn active={tab === "planner"} onClick={() => setTab("planner")} testid="tab-planner" icon={CalendarDays}>Day Planner</TabBtn>
        <TabBtn active={tab === "board"} onClick={() => setTab("board")} testid="tab-board" icon={LayoutGrid}>Task Board</TabBtn>
        <TabBtn active={tab === "crew"} onClick={() => setTab("crew")} testid="tab-crew" icon={UserPlus}>Crew</TabBtn>
        <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview" icon={Users}>Team Overview</TabBtn>
        <TabBtn active={tab === "turfmap"} onClick={() => setTab("turfmap")} testid="tab-turfmap" icon={MapIcon}>Turf Map</TabBtn>
        <TabBtn active={tab === "policies"} onClick={() => setTab("policies")} testid="tab-policies" icon={ShieldCheck}>Policies</TabBtn>
      </div>

      {tab === "invoices" && <InvoiceManager />}
      {tab === "checkins" && <CheckInPanel reload={reload} />}
      {tab === "planner" && <DayPlanner />}
      {tab === "board" && <TaskBoard />}
      {tab === "crew" && <CrewManager />}
      {tab === "overview" && <TeamOverview />}
      {tab === "turfmap" && <TurfMap clients={clients} reload={reload} />}
      {tab === "policies" && <PoliciesPanel />}
      {tab === "clients" && (
      <>
      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-2)]" />
        <input
          data-testid="client-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search clients…"
          className="w-full h-12 pl-11 pr-4 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-[var(--line)] bg-white p-16 text-center">
          <Users className="w-8 h-8 mx-auto text-[var(--text-2)]" />
          <p className="mt-4 text-sm text-[var(--text-2)]">{q ? "No clients match your search." : "No clients yet. Add your first client to start dispatching."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((c) => (
            <div key={c.id} data-testid="client-card" className="border border-[var(--line)] bg-white flex flex-col">
              <div className="p-5 border-b border-[var(--line)] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-serif text-xl tracking-tight truncate">{c.name}</h3>
                  {c.address && (
                    <p className="flex items-center gap-1.5 text-xs text-[var(--text-2)] mt-1 font-mono truncate">
                      <MapPin className="w-3 h-3 shrink-0" /> {c.address}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-[var(--text-2)] font-mono text-xs">
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                    {c.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {c.email}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid="client-edit" onClick={() => { setEditing(c); setClientDialog(true); }} className="px-2 h-8 text-xs border border-[var(--line)] hover:border-[var(--accent)] transition-colors">Edit</button>
                  <button data-testid="client-delete" onClick={() => removeClient(c)} className="w-8 h-8 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {c.notes && <p className="px-5 pt-4 text-sm text-[var(--text-2)] italic">“{c.notes}”</p>}

              <div className="p-5 flex-1">
                <p className="label-mono text-[var(--text-2)] flex items-center gap-2 mb-3">
                  <Wrench className="w-3.5 h-3.5" /> Active Service Loops
                </p>
                <div className="space-y-2">
                  {(c.loops || []).length === 0 && <p className="text-xs text-[var(--text-2)]">No service loops yet.</p>}
                  {(c.loops || []).map((l) => (
                    <div key={l.id} data-testid="loop-row" className="border border-[var(--line)] bg-[#f9f9f7] p-3 flex items-center gap-3 group">
                      <div className="w-1.5 self-stretch bg-[var(--accent)]" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{l.title}</p>
                        <div className="flex items-center gap-3 mt-1 text-[var(--text-2)] font-mono text-xs">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {l.time}</span>
                          <span className="flex items-center gap-1"><Repeat className="w-3 h-3" /> {l.recurrence_label}</span>
                          <span className="flex gap-1">
                            {l.channels?.map((ch) => { const I = CH_ICON[ch] || Mail; return <I key={ch} className="w-3 h-3" />; })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button data-testid="loop-edit" onClick={() => setEditLoop(l)} className="px-2 h-7 text-xs border border-[var(--line)] hover:border-[var(--accent)] transition-colors">Edit</button>
                        <button data-testid="loop-delete" onClick={() => removeLoop(l.id)} className="w-7 h-7 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  data-testid="add-loop"
                  onClick={() => setLoopFor(c)}
                  className="mt-3 w-full h-10 flex items-center justify-center gap-2 text-sm border border-dashed border-[var(--line)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add service loop
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      </>
      )}

      <ClientDialog open={clientDialog} onClose={() => setClientDialog(false)} onSaved={reload} client={editing} />
      <ServiceLoopDialog
        open={!!loopFor || !!editLoop}
        onClose={() => { setLoopFor(null); setEditLoop(null); }}
        onSaved={reload}
        client={loopFor}
        loop={editLoop}
      />
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon: Icon, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 h-10 px-4 text-sm border transition-colors shrink-0 whitespace-nowrap ${active ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}
    >
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}
